import React from 'react';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';

interface ConfirmationDeleteModalProps {
    show: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isDeleting: boolean;
    error: string | null;
    isLastRoom?: boolean;
}

const ConfirmationDeleteModal: React.FC<ConfirmationDeleteModalProps> = ({ show, onClose, onConfirm, isDeleting, error, isLastRoom = false }) => {

    const renderBody = () => {
        if (error) {
            return (
                <Alert variant="danger" className="d-flex align-items-center">
                    <IconAlertTriangle size={24} className="me-2" />
                    <div>
                        <strong>{isLastRoom ? 'Failed to delete reservation' : 'Failed to delete room'}</strong>
                        <p className="mb-0 small">{error}</p>
                    </div>
                </Alert>
            );
        }

        if (isDeleting) {
            return (
                <div className="text-center py-3">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 mb-0 fw-bold">
                        {isLastRoom ? 'Deleting entire reservation, please wait...' : 'Deleting room, please wait...'}
                    </p>
                </div>
            );
        }

        return isLastRoom ? (
            <Alert variant="warning" className="d-flex align-items-start">
                <IconAlertTriangle size={24} className="me-2 flex-shrink-0" />
                <div>
                    <strong>Warning: This is the last room</strong>
                    <p className="mb-0 small">
                        Deleting the last room will permanently delete the entire reservation. This action cannot be undone.
                    </p>
                </div>
            </Alert>
        ) : (
            <p>Are you sure you want to permanently delete this room from the reservation? This action cannot be undone.</p>
        );
    };

    const renderFooter = () => {
        if (error) {
            return (
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            );
        }

        return (
            <>
                <Button variant="secondary" onClick={onClose} disabled={isDeleting}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={onConfirm} disabled={isDeleting}>
                    {isDeleting ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                            <span className="ms-1">Deleting...</span>
                        </>
                    ) : (
                        <>
                            <IconTrash size={16} className='me-1' />
                            {isLastRoom ? 'Delete Reservation' : 'Confirm & Delete'}
                        </>
                    )}
                </Button>
            </>
        );
    };

    return (
        <Modal show={show} onHide={onClose} centered backdrop={isDeleting ? 'static' : true}>
            <Modal.Header closeButton={!isDeleting}>
                <Modal.Title>
                    {error ? 'Error' : isLastRoom ? 'Delete Entire Reservation' : 'Confirm Deletion'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {renderBody()}
            </Modal.Body>
            <Modal.Footer>
                {renderFooter()}
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmationDeleteModal;